import React, { useEffect, useState, useRef } from 'react';
import CuadroDivisionArgentina from './CuadroDivisionArgentina.js';
import MensajeCargando from '../UI/MensajeCargando.js';
import MensajeError from '../UI/MensajeError.js';
import useGetListaURL from '../../hooks/useGetListaURL.js';
import styles from './RecuadroArgentina.module.css';

const ID_ARGENTINA = 1;
const NIVEL_MAX_EXCLUIDO = 90;
const ANCHO_MINIMO_CUADRO = 14; // rem
const GAP = 0.8; // rem
const PADDING_TOTAL = 1.6; // padding left + right en rem

/**
 * Recuadro con las divisiones vigentes de Argentina (nivelActual < 90),
 * ordenadas por nivelActual e id. Cada división muestra los participantes
 * del torneo más reciente.
 *
 * El componente calcula dinámicamente cuántos cuadros caben en la primera fila
 * y expande automáticamente solo esos.
 */
const RecuadroArgentina = () => {
  const [divisiones, setDivisiones] = useState([]);
  const [expandido, setExpandido] = useState(true);
  const [cuadrosPrimeraFila, setCuadrosPrimeraFila] = useState(3);
  const grillaDivisionesRef = useRef(null);
  const { isCargando, erroresGetLista, fetchData, resetErroresGetLista } = useGetListaURL();

  useEffect(() => {
    resetErroresGetLista();
  }, [resetErroresGetLista]);

  useEffect(() => {
    fetchData('paises/' + ID_ARGENTINA + '/divisiones?vigente=true', (lista) => {
      const filtradas = (lista || [])
        .filter((d) => d.nivelActual == null || d.nivelActual < NIVEL_MAX_EXCLUIDO)
        .sort((a, b) => {
          const nivelA = a.nivelActual ?? 999;
          const nivelB = b.nivelActual ?? 999;
          if (nivelA !== nivelB) return nivelA - nivelB;
          return a.id - b.id;
        });
      setDivisiones(filtradas);
    });
  }, [fetchData]);

  // Calcular cuántos cuadros caben en la primera fila
  useEffect(() => {
    const calcularCuadrosPrimeraFila = () => {
      if (!grillaDivisionesRef.current) return;

      const anchoGrilla = grillaDivisionesRef.current.offsetWidth;
      const anchoRem =
        anchoGrilla / parseFloat(getComputedStyle(document.documentElement).fontSize);

      // Fórmula: (ancho - PADDING) / (ANCHO_MINIMO_CUADRO + GAP) - gap del último elemento
      // Redondeamos hacia abajo para obtener cuántos caben sin overflow
      const cuadrosCaben = Math.max(
        1,
        Math.floor((anchoRem - PADDING_TOTAL + GAP) / (ANCHO_MINIMO_CUADRO + GAP))
      );

      setCuadrosPrimeraFila(cuadrosCaben);
    };

    calcularCuadrosPrimeraFila();

    // Recalcular cuando cambia el tamaño de la ventana
    window.addEventListener('resize', calcularCuadrosPrimeraFila);
    return () => window.removeEventListener('resize', calcularCuadrosPrimeraFila);
  }, [expandido]);

  return (
    <div className={styles.recuadro}>
      <div className={styles.encabezado}>
        <span className={styles.titulo}>Argentina</span>
        <button
          className={styles.botonCerrar}
          onClick={() => setExpandido((v) => !v)}
          title={expandido ? 'Cerrar' : 'Abrir'}
          aria-expanded={expandido}
        >
          {expandido ? '▲' : '▼'}
        </button>
      </div>
      <div className={expandido ? undefined : styles.contenidoOculto}>
        <MensajeCargando isCargando={isCargando} />
        <MensajeError errores={erroresGetLista} />
        {!isCargando && (
          <div className={styles.grillaDivisiones} ref={grillaDivisionesRef}>
            {divisiones.map((division, index) => (
              <CuadroDivisionArgentina
                key={division.id}
                division={division}
                debeExpandirseAutomaticamente={index < cuadrosPrimeraFila}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RecuadroArgentina;
